// /objects/:objectId — one object-capture job. While the GPU box works: a stage-pipeline
// progress UI. On completion: a full orbit/zoom viewer — streamed model.ply download with
// progress, worker parse (plyGaussians.worker), drei <Splat> inside the [Math.PI,0,0]
// flip-cancel group, camera framed from object-meta.json (fallback: parsed stats), dark
// studio backdrop + a soft contact-shadow disc under the object.
import { Component, Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Splat, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import {
  ApiError,
  getObjectJob,
  type ObjectJob,
  type ObjectMeta,
  type SplatModelVariant,
} from "../lib/apiClient";
import type { GaussianStats } from "../splat/plyGaussians";
import { ShSplatScene } from "../splat/ShSplatScene";
import { defaultVariantId, deriveCaptureMode, isShMode } from "../lib/captureModes";
import { OBJECT_STAGES, objectStageCopy } from "../lib/objectStages";
import { Breadcrumbs, Spinner } from "./ui";

const DARK_BACKDROP = "radial-gradient(ellipse at 50% 32%, #2a2c33 0%, #0c0c10 72%)";
const fmtN = (n: number) => n.toLocaleString();

// ---------------------------------------------------------------------------
// Stage pipeline (queued / processing)

function StagePipeline({ stage, pct, detail }: { stage?: string; pct?: number; detail?: string }) {
  const idx = stage ? (OBJECT_STAGES as readonly string[]).indexOf(stage) : -1;
  const pctVal = typeof pct === "number" ? Math.max(0, Math.min(100, pct)) : null;
  return (
    <div className="card space-y-4 p-6">
      <div className="flex flex-wrap gap-1.5">
        {OBJECT_STAGES.map((s, i) => (
          <span
            key={s}
            className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition ${
              i === idx
                ? "bg-warm text-white shadow-card"
                : i < idx
                  ? "bg-cream text-ink/70"
                  : "bg-cream/50 text-muted/70"
            }`}
          >
            {i < idx ? "✓ " : ""}
            {s}
          </span>
        ))}
      </div>
      <div>
        <p className="font-medium text-ink">{objectStageCopy(stage)}</p>
        {detail && <p className="text-sm text-muted">{detail}</p>}
      </div>
      {pctVal !== null && (
        <div>
          <div className="h-2 overflow-hidden rounded-full bg-cream">
            <div
              className="h-full rounded-full bg-warm transition-all"
              style={{ width: `${pctVal}%` }}
            />
          </div>
          <p className="mt-1 text-right text-xs text-muted">{Math.round(pctVal)}%</p>
        </div>
      )}
      <p className="text-xs text-muted">
        Training a 3D model takes a while (typically 20–40 minutes). You can leave — the job keeps
        running and this page updates on its own.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completed viewer

// Only what the viewer actually reads. The worker also posts a colors Float32Array —
// deliberately NOT retained. positions IS kept (3 floats/gaussian, ~1.7 MB at object
// scale): it's the pick buffer for double-click-to-focus, since a gaussian splat can't
// be raycast like a mesh.
interface Parsed {
  /** Packed antimatter15 .splat bytes for drei <Splat> (PLY → .splat done in the worker). */
  splat: Uint8Array;
  positions: Float32Array;
  up: [number, number, number];
  stats: GaussianStats;
}

function parseMeta(j: unknown): ObjectMeta | null {
  if (!j || typeof j !== "object") return null;
  const m = j as Record<string, unknown>;
  const v3 = (a: unknown): a is [number, number, number] =>
    Array.isArray(a) && a.length >= 3 && a.slice(0, 3).every((x) => typeof x === "number" && Number.isFinite(x));
  if (!v3(m.center) || !v3(m.up) || typeof m.radius !== "number" || !(m.radius > 0)) return null;
  const bbox = (m.bbox ?? null) as { min?: unknown; max?: unknown } | null;
  const counts = (m.counts ?? null) as { trained?: unknown; final?: unknown } | null;
  const scene = (m.scene ?? null) as { pathCentroid?: unknown } | null;
  return {
    version: typeof m.version === "number" ? m.version : undefined,
    captureMode: typeof m.captureMode === "string" ? m.captureMode : undefined,
    scene:
      scene && v3(scene.pathCentroid)
        ? { pathCentroid: [scene.pathCentroid[0], scene.pathCentroid[1], scene.pathCentroid[2]] }
        : undefined,
    center: [m.center[0], m.center[1], m.center[2]],
    up: [m.up[0], m.up[1], m.up[2]],
    radius: m.radius,
    bbox:
      bbox && v3(bbox.min) && v3(bbox.max)
        ? { min: [bbox.min[0], bbox.min[1], bbox.min[2]], max: [bbox.max[0], bbox.max[1], bbox.max[2]] }
        : undefined,
    counts: counts
      ? {
          trained: typeof counts.trained === "number" ? counts.trained : undefined,
          final: typeof counts.final === "number" ? counts.final : undefined,
        }
      : undefined,
  };
}

class ViewerBoundary extends Component<{ children: ReactNode }, { err: string | null }> {
  state: { err: string | null } = { err: null };
  static getDerivedStateFromError(e: unknown) {
    return { err: e instanceof Error ? e.message : String(e) };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-red-400">
          <span className="text-2xl">⚠️</span>
          <span>The 3D viewer hit a snag: {this.state.err}</span>
          <span className="text-white/50">Reload the page to try again.</span>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Soft contact shadow: a circle perpendicular to `up` at the object's lowest point, with a
 *  canvas-generated radial-gradient alpha (0.45 → transparent). */
function ShadowDisc({ center, up, radius }: { center: [number, number, number]; up: THREE.Vector3; radius: number }) {
  const texture = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = c.height = 256;
    const g = c.getContext("2d");
    if (g) {
      const grad = g.createRadialGradient(128, 128, 8, 128, 128, 128);
      grad.addColorStop(0, "rgba(0,0,0,0.45)");
      grad.addColorStop(0.55, "rgba(0,0,0,0.16)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = grad;
      g.fillRect(0, 0, 256, 256);
    }
    return new THREE.CanvasTexture(c);
  }, []);
  useEffect(() => () => texture.dispose(), [texture]);
  const quaternion = useMemo(
    () => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), up),
    [up],
  );
  return (
    <mesh name="shadow-disc" position={center} quaternion={quaternion} renderOrder={-1}>
      <circleGeometry args={[radius, 64]} />
      <meshBasicMaterial map={texture} transparent depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  );
}

/** Photogrammetry-style mesh variant (object-mesh.glb). Lit standard materials. */
function ObjectMesh({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  return <primitive object={scene} />;
}

/** Double-click-to-focus orbit rig. Splats can't be raycast, so picking runs against the
 *  parsed gaussian centers: among points within a small screen-space cone around the
 *  click ray, take the one nearest the camera (the front surface). A hit glides the
 *  orbit target there; a miss (background) or the Reset button glides everything back
 *  to the default framing. Mesh mode uses a real raycast instead. */
function InspectRig({
  positions,
  mode,
  focus,
  onPick,
  defaults,
  resetNonce,
}: {
  positions: Float32Array | null;
  mode: "splat" | "mesh";
  focus: [number, number, number] | null;
  onPick: (p: [number, number, number] | null) => void;
  defaults: { target: [number, number, number]; position: [number, number, number] };
  resetNonce: number;
}) {
  const camera = useThree((st) => st.camera);
  const gl = useThree((st) => st.gl);
  const scene = useThree((st) => st.scene);
  const controls = useThree((st) => st.controls) as unknown as {
    target: THREE.Vector3;
    update: () => void;
  } | null;
  const resettingRef = useRef(false);

  useEffect(() => {
    if (resetNonce > 0) resettingRef.current = true;
  }, [resetNonce]);

  useEffect(() => {
    const el = gl.domElement;
    const onDblClick = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -(((e.clientY - r.top) / r.height) * 2 - 1),
      );
      const ray = new THREE.Raycaster();
      ray.setFromCamera(ndc, camera);
      if (mode === "mesh") {
        const hits = ray
          .intersectObjects(scene.children, true)
          .filter((h) => h.object.name !== "shadow-disc");
        onPick(hits.length ? [hits[0].point.x, hits[0].point.y, hits[0].point.z] : null);
        return;
      }
      if (!positions) return;
      // Point pick: nearest-to-camera point within ~9px of the click ray. Positions are
      // in the raw PLY frame, which IS the scene frame (drei's internal flip and the
      // [Math.PI,0,0] wrapper cancel), so no transform is needed.
      const o = ray.ray.origin;
      const d = ray.ray.direction;
      const fovRad = ((camera as THREE.PerspectiveCamera).fov * Math.PI) / 180;
      const tanTol = (9 / r.height) * 2 * Math.tan(fovRad / 2);
      let best = -1;
      let bestT = Infinity;
      for (let i = 0; i < positions.length; i += 3) {
        const vx = positions[i] - o.x;
        const vy = positions[i + 1] - o.y;
        const vz = positions[i + 2] - o.z;
        const t = vx * d.x + vy * d.y + vz * d.z;
        if (t <= 0 || t >= bestT) continue;
        const perp2 = vx * vx + vy * vy + vz * vz - t * t;
        const tol = tanTol * t;
        if (perp2 <= tol * tol) {
          bestT = t;
          best = i;
        }
      }
      onPick(best >= 0 ? [positions[best], positions[best + 1], positions[best + 2]] : null);
    };
    el.addEventListener("dblclick", onDblClick);
    return () => el.removeEventListener("dblclick", onDblClick);
  }, [gl, camera, scene, mode, positions, onPick]);

  useFrame(() => {
    if (!controls) return;
    const want = focus ?? defaults.target;
    controls.target.lerp(new THREE.Vector3(want[0], want[1], want[2]), 0.14);
    if (resettingRef.current && !focus) {
      const home = new THREE.Vector3(defaults.position[0], defaults.position[1], defaults.position[2]);
      camera.position.lerp(home, 0.1);
      if (camera.position.distanceTo(home) < 1e-3) resettingRef.current = false;
    }
  });
  return null;
}

function ObjectViewer({ job }: { job: ObjectJob }) {
  const models = useMemo<SplatModelVariant[]>(
    () =>
      job.models && job.models.length > 0
        ? job.models
        : job.modelUrl
          ? [{ id: "model.ply", label: "Default", bytes: null, url: job.modelUrl }]
          : [],
    [job.models, job.modelUrl],
  );
  const [variantId, setVariantId] = useState<string | null>(null);

  const [mode, setMode] = useState<"splat" | "mesh">("splat");
  const [autoRotate, setAutoRotate] = useState(true);
  // Click-to-focus: the orbit pivot the user picked (null = default framing).
  const [focus, setFocus] = useState<[number, number, number] | null>(null);
  const [resetNonce, setResetNonce] = useState(0);
  const [data, setData] = useState<Parsed | null>(null);
  const [progress, setProgress] = useState(0);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [meta, setMeta] = useState<ObjectMeta | null>(null);
  const [metaSettled, setMetaSettled] = useState(false);

  // Scene captures render without the contact-shadow disc and default to the full-SH
  // variant (the SH-capable scene renderer evaluates it; object mode keeps SH0 first).
  // Absent/junk modes totalize to "object" — existing jobs render as before.
  const captureMode = deriveCaptureMode(job, meta);
  const variant =
    models.find((m) => m.id === variantId) ??
    models.find((m) => m.id === defaultVariantId(captureMode, models, true)) ??
    models[0] ??
    null;
  // Scene mode renders `variant` through GaussianSplats3D (SH degree <= 2, its own fetch)
  // while the worker ALWAYS parses the small SH0 model.ply: it stays the click-to-focus
  // pick buffer + stats source (identical gaussian set/order — postprocess writes both
  // files from one array) and the instant fallback render while/if the SH path loads/fails.
  const pickVariant =
    isShMode(captureMode) ? (models.find((m) => m.id === "model.ply") ?? variant) : variant;
  const [shState, setShState] = useState<"loading" | "ready" | "failed">("loading");
  const [shProgress, setShProgress] = useState(0);
  useEffect(() => {
    setShState("loading");
    setShProgress(0);
  }, [job.objectId, variant?.id, captureMode]);
  const useShRenderer = isShMode(captureMode) && shState !== "failed" && mode === "splat";
  // Presigned URL captured once per (job, variant): react-query refetches mint new URL
  // strings, and re-keying the SH loader on those would re-download the whole file.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shUrl = useMemo(() => variant?.url ?? null, [job.objectId, variant?.id]);

  // object-meta.json — authoritative camera framing (tiny; lands well before the .ply).
  useEffect(() => {
    if (!job.metaUrl) {
      setMeta(null);
      setMetaSettled(true);
      return;
    }
    let cancelled = false;
    setMetaSettled(false);
    fetch(job.metaUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return;
        setMeta(parseMeta(j));
        setMetaSettled(true);
      })
      .catch(() => {
        if (cancelled) return;
        setMeta(null);
        setMetaSettled(true); // fall back to worker stats
      });
    return () => {
      cancelled = true;
    };
  }, [job.metaUrl]);

  // Latest variant, readable at effect-run time. Every react-query refetch mints NEW presigned
  // URLs (new variant object identities), so the download effect below must NOT key on the
  // variant object — it keys on the stable [objectId, variant id] and reads the current URL here.
  const variantRef = useRef(pickVariant);
  variantRef.current = pickVariant;

  // Download the pick-buffer .ply with streamed progress, parse off-thread in the worker.
  // Keyed on the stable variant ID so refetches/reconnects don't re-download the model.
  // (Object mode: the displayed variant. Scene mode: always the small SH0 model.ply —
  // the SH renderer fetches the displayed variant itself.)
  useEffect(() => {
    const v = variantRef.current;
    if (!v) return;
    let cancelled = false;
    const ac = new AbortController();
    setData(null);
    setParseErr(null);
    setProgress(0);
    const worker = new Worker(new URL("../splat/plyGaussians.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent) => {
      if (cancelled) return;
      if (e.data.ok) {
        // The worker also sends colors; positions are kept as the click-to-focus pick buffer.
        setData({ splat: e.data.splat, positions: e.data.positions, up: e.data.up, stats: e.data.stats });
      } else setParseErr(e.data.error);
      worker.terminate();
    };
    (async () => {
      try {
        const resp = await fetch(v.url, { signal: ac.signal });
        if (!resp.ok) throw new Error(`download failed (${resp.status})`);
        const total = v.bytes ?? Number(resp.headers.get("content-length")) ?? 0;
        let buf: ArrayBuffer;
        if (resp.body && total > 0) {
          const reader = resp.body.getReader();
          const chunks: Uint8Array[] = [];
          let got = 0;
          for (;;) {
            if (cancelled) {
              reader.cancel().catch(() => {});
              return;
            }
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            got += value.length;
            if (!cancelled) setProgress(got / total);
          }
          const merged = new Uint8Array(got);
          let o = 0;
          for (const c of chunks) {
            merged.set(c, o);
            o += c.length;
          }
          buf = merged.buffer;
        } else {
          buf = await resp.arrayBuffer();
        }
        if (!cancelled) worker.postMessage(buf, [buf]);
      } catch (e) {
        if (cancelled || (e instanceof DOMException && e.name === "AbortError")) return;
        setParseErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
      worker.terminate();
    };
  }, [job.objectId, pickVariant?.id]);

  // drei <Splat> consumes the packed .splat bytes via a blob URL.
  const splatUrl = useMemo(
    () => (data?.splat ? URL.createObjectURL(new Blob([data.splat as BlobPart])) : null),
    [data?.splat],
  );
  useEffect(() => () => {
    if (splatUrl) URL.revokeObjectURL(splatUrl);
  }, [splatUrl]);

  // Camera framing. object-meta center/up/radius (and the worker stats fallback) are in the raw
  // PLY frame — and the RENDERED splat lands in that same frame: drei's <Splat> internally maps
  // packed centers (x,y,z)→(x,−y,−z) and the [Math.PI,0,0] wrapper group applies the inverse, so
  // the net transform is identity (SplatInspectorView frames from unflipped stats for the same
  // reason). So center/up are used as-is, NOT flipped.
  // Works from meta alone (no parsed splat needed) so mesh mode can render without the .ply.
  const view = useMemo(() => {
    if (!metaSettled) return null;
    const c = meta?.center ?? data?.stats.centroid;
    const upRaw = meta?.up ?? data?.up;
    if (!c || !upRaw) return null;
    const radius =
      meta && meta.radius > 0
        ? meta.radius
        : data
          ? Math.max(0.25, 0.5 * Math.max(...data.stats.coreDims))
          : null;
    if (radius === null) return null;
    const target = new THREE.Vector3(c[0], c[1], c[2]);
    const up = new THREE.Vector3(upRaw[0], upRaw[1], upRaw[2]);
    if (up.lengthSq() < 1e-12) up.set(0, 1, 0);
    up.normalize();
    // A horizontal direction (perpendicular to up) to place the camera off to one side.
    // Scene captures are partial arcs: an arbitrary azimuth can face the UNSEEN cone
    // (pure haze), so when the meta carries the camera-path centroid, home the camera on
    // the path's side of the subject — by construction a direction the capture covered.
    let horiz = new THREE.Vector3(1, 0, 0);
    const pc = meta?.scene?.pathCentroid;
    if (pc) {
      const toPath = new THREE.Vector3(pc[0], pc[1], pc[2]).sub(target);
      toPath.sub(up.clone().multiplyScalar(up.dot(toPath)));
      if (toPath.lengthSq() > 1e-12) horiz = toPath;
    }
    if (Math.abs(up.dot(horiz.clone().normalize())) > 0.9) horiz = new THREE.Vector3(0, 0, 1);
    horiz.sub(up.clone().multiplyScalar(up.dot(horiz))).normalize();
    const position = target
      .clone()
      .add(horiz.multiplyScalar(2.1 * radius))
      .add(up.clone().multiplyScalar(0.55 * radius));
    // Shadow disc sits at the bbox's lowest point along up (tiny offset against z-fighting).
    const bmin = meta?.bbox?.min ?? data?.stats.bboxMin;
    const bmax = meta?.bbox?.max ?? data?.stats.bboxMax;
    let tmin = Infinity;
    if (bmin && bmax) {
      for (const x of [bmin[0], bmax[0]])
        for (const y of [bmin[1], bmax[1]])
          for (const z of [bmin[2], bmax[2]]) tmin = Math.min(tmin, x * up.x + y * up.y + z * up.z);
    } else {
      // meta without bbox and no parsed splat yet: bounding-sphere lower bound.
      tmin = target.dot(up) - radius;
    }
    const discCenter = target
      .clone()
      .add(up.clone().multiplyScalar(tmin - target.dot(up) - 0.015 * radius));
    return {
      target: target.toArray() as [number, number, number],
      position: position.toArray() as [number, number, number],
      up: up.toArray() as [number, number, number],
      upVec: up,
      radius,
      near: radius / 100,
      far: radius * 60,
      discCenter: discCenter.toArray() as [number, number, number],
      discRadius: 0.9 * radius,
    };
  }, [data, meta, metaSettled]);

  useEffect(() => {
    setFocus(null);
  }, [variant?.id, mode]);

  const count = data?.stats.count ?? meta?.counts?.final ?? null;
  const downloadUrl = variant?.url ?? job.modelUrl;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {job.meshUrl && (
          <div className="flex rounded-full bg-cream p-1 text-sm">
            {(["splat", "mesh"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-full px-4 py-1.5 font-medium capitalize transition ${
                  mode === m ? "bg-white text-accent shadow-card" : "text-muted"
                }`}
              >
                {m === "splat" ? "Splat" : "Mesh"}
              </button>
            ))}
          </div>
        )}
        {models.length > 1 && (
          <select
            className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm"
            value={variant?.id ?? ""}
            onChange={(e) => setVariantId(e.target.value)}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.bytes ? ` · ${(m.bytes / 1e6).toFixed(0)} MB` : ""}
              </option>
            ))}
          </select>
        )}
        {downloadUrl && (
          <a className="btn-ghost text-sm" href={downloadUrl} download>
            Download .ply
          </a>
        )}
        {count !== null && <span className="text-xs text-muted">{fmtN(count)} gaussians</span>}
        {focus && (
          <button
            className="btn-ghost text-sm"
            onClick={() => {
              setFocus(null);
              setResetNonce((n) => n + 1);
            }}
          >
            ⟲ Reset view
          </button>
        )}
        <span className="text-xs text-muted/70">double-click to orbit a spot · background resets</span>
      </div>

      <div
        className="card relative overflow-hidden"
        style={{ height: "calc(100vh - 220px)", minHeight: 520, background: DARK_BACKDROP }}
        onPointerDown={() => setAutoRotate(false)}
      >
        <ViewerBoundary>
          {view && (splatUrl || (mode === "mesh" && job.meshUrl)) ? (
            <Canvas
              key={variant?.id /* refit the camera when the variant switches */}
              camera={{ position: view.position, up: view.up, fov: 45, near: view.near, far: view.far }}
              dpr={[1, 2]}
              gl={{ alpha: true }}
            >
              <OrbitControls
                makeDefault
                target={view.target}
                enableDamping
                autoRotate={autoRotate}
                autoRotateSpeed={0.8}
                zoomToCursor
                minDistance={0.05 * view.radius}
                maxDistance={8 * view.radius}
              />
              <InspectRig
                positions={data?.positions ?? null}
                mode={mode === "mesh" && job.meshUrl ? "mesh" : "splat"}
                focus={focus}
                onPick={(pt) => {
                  setAutoRotate(false);
                  if (pt) {
                    setFocus(pt);
                  } else {
                    setFocus(null);
                    setResetNonce((n) => n + 1);
                  }
                }}
                defaults={{ target: view.target, position: view.position }}
                resetNonce={resetNonce}
              />
              {mode === "mesh" && job.meshUrl ? (
                <>
                  <ambientLight intensity={1.1} />
                  <directionalLight position={[3, 6, 3]} intensity={1.2} />
                  <Suspense fallback={null}>
                    <ObjectMesh url={job.meshUrl} />
                  </Suspense>
                </>
              ) : splatUrl ? (
                <Suspense fallback={null}>
                  {/* SH0 packed path: object mode always; scene mode shows it instantly and
                      keeps it unless/until the SH renderer is ready (and permanently if that
                      fails — same gaussians, flat color). drei renders the splat rotated 180°
                      about X (it stores center as (x,−y,−z)); the group applies the inverse
                      so the splat lands in the PLY frame. */}
                  {!(useShRenderer && shState === "ready") && (
                    <group rotation={[Math.PI, 0, 0]}>
                      <Splat src={splatUrl} />
                    </group>
                  )}
                  {useShRenderer && shUrl && (
                    <ShSplatScene
                      key={`${job.objectId}:${variant?.id}`}
                      url={shUrl}
                      onProgress={setShProgress}
                      onReady={() => setShState("ready")}
                      onError={() => setShState("failed")}
                    />
                  )}
                </Suspense>
              ) : null}
              {/* Scene fragments carry their own ground — a synthetic contact shadow
                  under the whole scene reads as a floating platform, so object-only. */}
              {captureMode === "object" && (
                <ShadowDisc center={view.discCenter} up={view.upVec} radius={view.discRadius} />
              )}
            </Canvas>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-white/60">
              {parseErr ? (
                <span className="px-6 text-center text-sm text-red-400">
                  Couldn't load the model: {parseErr}
                </span>
              ) : (
                <>
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/60 border-t-transparent" />
                  <span className="text-sm">
                    {progress > 0 ? `Downloading model… ${Math.round(progress * 100)}%` : "Loading model…"}
                  </span>
                </>
              )}
            </div>
          )}
        </ViewerBoundary>
        {view && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 text-xs text-white/60">
            <span>drag to orbit · scroll to zoom</span>
            <span>
              {mode === "mesh"
                ? "polygon mesh"
                : isShMode(captureMode)
                  ? useShRenderer && shState === "loading"
                    ? `${captureMode} splat · full detail ${Math.round(shProgress * 100)}%`
                    : useShRenderer && shState === "ready"
                      ? `${captureMode} splat · full detail`
                      : `${captureMode} splat`
                  : "gaussian splat"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page

const isTerminal = (s: string) => s === "completed" || s === "failed";

export function ObjectDetailView() {
  const { objectId = "" } = useParams();
  const query = useQuery<ObjectJob>({
    queryKey: ["object", objectId],
    queryFn: () => getObjectJob(objectId),
    enabled: !!objectId,
    refetchInterval: (q) => {
      // An errored query (e.g. 404) has no status — don't poll it forever.
      if (q.state.error) return false;
      const s = (q.state.data as ObjectJob | undefined)?.status;
      return s && isTerminal(s) ? false : 6000;
    },
  });
  const job = query.data;
  const title = job?.label || "Object";

  return (
    <div className="space-y-4">
      <Breadcrumbs items={[{ label: "3D Objects", to: "/objects" }, { label: title }]} />
      <h1 className="text-2xl font-bold text-ink">{title}</h1>

      {query.isLoading ? (
        <Spinner />
      ) : query.error ? (
        <div className="card p-4 text-sm text-red-500">
          {query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 410)
            ? "This object wasn't found — it may have been deleted, or the link is wrong."
            : `Couldn't load this object: ${query.error instanceof Error ? query.error.message : "unknown error"}`}
        </div>
      ) : !job ? null : job.status === "completed" ? (
        <ObjectViewer job={job} />
      ) : job.status === "failed" ? (
        <div className="card space-y-2 p-6">
          <p className="font-semibold text-red-500">Processing failed</p>
          {job.error && <p className="text-sm text-ink">{job.error}</p>}
          {job.detail && <p className="text-sm text-muted">{job.detail}</p>}
          <p className="text-sm text-muted">
            Try again with a slower, steadier walk-around — one full loop with the whole object in
            frame works best.
          </p>
        </div>
      ) : job.status === "awaiting-upload" ? (
        <div className="card p-6 text-sm text-muted">
          Waiting for the upload to finish. If you closed the page mid-upload, start a new capture
          from the Objects page.
        </div>
      ) : (
        // status queued/processing — i.e. past awaiting-upload: /start HeadObject-verified the
        // media on the server, so landing here IS confirmation the upload succeeded. Say so.
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 font-semibold text-green-700">
              ✓ Upload confirmed
            </span>
            <span className="text-muted">Your capture is safely on the server.</span>
          </div>
          {/* No stage yet (e.g. status "queued") → fall back to the status so objectStageCopy's
              queued line ("Queued — waiting for a GPU…") shows instead of the generic copy. */}
          <StagePipeline stage={job.stage ?? job.status} pct={job.pct} detail={job.detail} />
        </div>
      )}
    </div>
  );
}
